import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Zap, ZapOff, AlertTriangle, Loader2, Play, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

/**
 * Floating "⚡ Lightning Delay" pill the coach taps when a game pauses for weather.
 *
 * 3 visual states:
 *   1. No active delay → amber "⚡ Lightning Delay" button
 *   2. Delay in progress → red pulsing "DELAYED · 23 min left" + Tap to manage
 *   3. Tapping while in progress → dialog to clear (game resumed) or extend
 *
 * Auto-broadcasts an urgent message to the team chat + parent feed.
 */
const PRESETS = [
  { minutes: 15, label: '15 min', hint: 'Quick storm cell' },
  { minutes: 30, label: '30 min', hint: 'Standard lightning delay' },
  { minutes: 45, label: '45 min', hint: 'Heavy rain / waiting on field' },
  { minutes: 60, label: '60 min', hint: 'Severe weather' },
];

const useCountdown = (iso) => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!iso) { setRemaining(0); return undefined; }
    const tick = () => {
      const end = new Date(iso).getTime();
      const r = Math.max(0, Math.round((end - Date.now()) / 1000));
      setRemaining(r);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return remaining;
};

const formatRemaining = (sec) => {
  if (sec <= 0) return 'Resume time reached';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function LightningDelayButton({ gameId, opponentName, lightningDelay, onSaved, className = '' }) {
  const [open, setOpen] = useState(false);
  const [savingMode, setSavingMode] = useState(null); // 'delay' | 'cancel' | 'clear' | null
  const [note, setNote] = useState('');
  const [selectedMinutes, setSelectedMinutes] = useState(30);

  const active = !!lightningDelay && lightningDelay.status === 'delayed';
  const canceled = !!lightningDelay && lightningDelay.status === 'canceled';
  const remaining = useCountdown(active ? lightningDelay?.resume_at : null);

  // Reset draft state whenever dialog opens
  useEffect(() => {
    if (open) {
      setNote(lightningDelay?.note || '');
      setSelectedMinutes(lightningDelay?.delay_minutes || 30);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeclareDelay = async () => {
    if (!gameId) return;
    setSavingMode('delay');
    try {
      const r = await api.post(`/games/${gameId}/lightning-delay`, {
        status: 'delayed',
        delay_minutes: selectedMinutes,
        note: note.trim() || null,
      });
      onSaved?.({ lightning_delay: r.data?.delay });
      toast.success(`Delay broadcast — every parent has been notified.`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the broadcast');
    } finally {
      setSavingMode(null);
    }
  };

  const handleCancelGame = async () => {
    if (!gameId) return;
    if (!window.confirm("Cancel today's game?\n\nThis posts an urgent 'game called' message to every parent.")) return;
    setSavingMode('cancel');
    try {
      const r = await api.post(`/games/${gameId}/lightning-delay`, {
        status: 'canceled',
        note: note.trim() || null,
      });
      onSaved?.({ lightning_delay: r.data?.delay });
      toast.success('Cancellation broadcast — parents notified.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not broadcast cancellation');
    } finally {
      setSavingMode(null);
    }
  };

  const handleClear = async () => {
    if (!gameId) return;
    setSavingMode('clear');
    try {
      await api.delete(`/games/${gameId}/lightning-delay`);
      onSaved?.({ lightning_delay: null });
      toast.success('Play resumed — parents notified.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear delay');
    } finally {
      setSavingMode(null);
    }
  };

  const buttonLabel = useMemo(() => {
    if (canceled) return 'Game called';
    if (active) {
      if (remaining > 0) return `DELAYED · ${formatRemaining(remaining)}`;
      return 'DELAYED · Manage';
    }
    return 'Lightning Delay';
  }, [active, canceled, remaining]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="lightning-delay-btn"
        className={cn(
          'fixed z-40 top-3 left-3 md:top-5 md:left-5 inline-flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg text-xs font-extrabold uppercase tracking-wider transition-all border-2',
          active
            ? 'bg-red-600 text-white border-red-300 animate-pulse'
            : canceled
              ? 'bg-stone-900 text-amber-300 border-amber-500'
              : 'bg-amber-500 text-stone-900 border-amber-200 hover:bg-amber-400',
          className,
        )}
      >
        {active ? <AlertTriangle size={14} className="text-amber-200" /> : <Zap size={14} />}
        <span data-testid="lightning-delay-label">{buttonLabel}</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => !savingMode && setOpen(v)}>
        <DialogContent className="max-w-md" data-testid="lightning-delay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="text-amber-500" size={18} />
              {active ? 'Lightning delay in progress' : canceled ? 'Game canceled' : 'Declare a lightning delay'}
            </DialogTitle>
            <DialogDescription>
              {active
                ? 'Parents see a banner with a live countdown. When the field reopens, tap Play resumed.'
                : canceled
                  ? 'A cancellation message has already been broadcast to every parent.'
                  : 'One tap broadcasts an urgent message to every parent on this team — no manual texting required.'}
            </DialogDescription>
          </DialogHeader>

          {/* ── ACTIVE STATE: countdown + clear ── */}
          {active && (
            <div className="space-y-4" data-testid="lightning-active-panel">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-[11px] uppercase tracking-wider text-red-700 font-bold mb-0.5">Targeting resume in</p>
                <p className="text-4xl font-extrabold text-red-700 tabular-nums" data-testid="lightning-countdown">
                  {formatRemaining(remaining)}
                </p>
                {lightningDelay.resume_at && (
                  <p className="text-[11px] text-red-700/70 mt-1">
                    ≈ {new Date(lightningDelay.resume_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </p>
                )}
                {lightningDelay.note && (
                  <p className="text-xs italic text-stone-600 mt-2">&ldquo;{lightningDelay.note}&rdquo;</p>
                )}
              </div>

              <Button
                onClick={handleClear}
                disabled={!!savingMode}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-12"
                data-testid="lightning-clear-btn"
              >
                {savingMode === 'clear' ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Play size={16} className="mr-1" />}
                Play resumed — back on the field!
              </Button>

              <button
                onClick={handleCancelGame}
                disabled={!!savingMode}
                className="w-full text-xs text-stone-500 hover:text-red-600 underline-offset-2 hover:underline disabled:opacity-50"
                data-testid="lightning-cancel-game-btn"
              >
                Call the game · Cancel today's matchup
              </button>
            </div>
          )}

          {/* ── CANCELED STATE: clear-only ── */}
          {canceled && (
            <div className="space-y-3" data-testid="lightning-canceled-panel">
              <div className="bg-stone-100 border border-stone-300 rounded-xl p-4 text-center">
                <ZapOff size={28} className="text-stone-500 mx-auto mb-1" />
                <p className="text-sm font-bold text-stone-700">Game called by coach</p>
                {lightningDelay.note && (
                  <p className="text-xs italic text-stone-500 mt-1">&ldquo;{lightningDelay.note}&rdquo;</p>
                )}
              </div>
              <Button
                onClick={handleClear}
                disabled={!!savingMode}
                variant="outline"
                className="w-full"
                data-testid="lightning-undo-cancel-btn"
              >
                Undo — game is back on
              </Button>
            </div>
          )}

          {/* ── DEFAULT STATE: pick preset + send ── */}
          {!active && !canceled && (
            <div className="space-y-4" data-testid="lightning-form-panel">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-stone-600">How long?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.minutes}
                      type="button"
                      onClick={() => setSelectedMinutes(p.minutes)}
                      data-testid={`lightning-preset-${p.minutes}`}
                      className={cn(
                        'rounded-xl p-3 text-left border-2 transition-all',
                        selectedMinutes === p.minutes
                          ? 'border-amber-500 bg-amber-50 shadow-md'
                          : 'border-stone-200 bg-white hover:border-amber-300',
                      )}
                    >
                      <p className="text-lg font-extrabold text-stone-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {p.label}
                      </p>
                      <p className="text-[11px] text-stone-500 leading-tight">{p.hint}</p>
                    </button>
                  ))}
                </div>

                <div className="mt-2">
                  <Label className="text-[11px] text-stone-500">Or custom (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={480}
                    value={selectedMinutes}
                    onChange={(e) => setSelectedMinutes(parseInt(e.target.value, 10) || 30)}
                    className="mt-1 h-9"
                    data-testid="lightning-minutes-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-stone-600">Note for parents <span className="text-stone-400 font-normal normal-case">(optional)</span></Label>
                <Input
                  placeholder='e.g., "Field flooded, waiting for crew"'
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={140}
                  className="mt-1"
                  data-testid="lightning-note-input"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-stone-700">
                <p className="font-bold flex items-center gap-1 mb-0.5">
                  <Clock size={11} /> What parents will see:
                </p>
                <p className="italic">
                  &ldquo;⚡ LIGHTNING DELAY — game vs {opponentName || 'opponent'} paused for ~{selectedMinutes} min. Targeting resume ≈ {new Date(Date.now() + selectedMinutes * 60_000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.{note ? ` Note: ${note}` : ''}&rdquo;
                </p>
              </div>

              <Button
                onClick={handleDeclareDelay}
                disabled={!!savingMode || !selectedMinutes}
                className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-extrabold h-12 text-base"
                data-testid="lightning-broadcast-btn"
              >
                {savingMode === 'delay' ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Zap size={16} className="mr-1" />}
                Broadcast delay to all parents
              </Button>

              <button
                onClick={handleCancelGame}
                disabled={!!savingMode}
                className="w-full text-xs text-stone-500 hover:text-red-600 underline-offset-2 hover:underline disabled:opacity-50"
                data-testid="lightning-call-game-btn"
              >
                Skip the delay — call the game now
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
